import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { IconButton, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { getSearchData } from "../utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import { showPopupModal } from "@widgets/CommonModal";
import { StyleSettings } from "../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { showDialog } from "@widgets/CommonDialog";
import { ClearIcon } from "@mui/x-date-pickers";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { global } from "@shared/libs/Global";
import SnLabel from "./SnLabel";
import { onSearchStore } from "@shared/libs/Global";

interface SnItemDetailBox2Props {
  defaultValue?: any;
  item_name?: any;
  dc_item_spec?: any;
  dc_item_unit?: any;
  nm_model?: any;
  dc_rmk?: any;
  code: string;
  cd_clas?: string;
  onValueChanged?: (code: string, name: string, rowData?: any) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  searchRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnItemDetailBox2 = React.memo(
  ({
    code,
    cd_clas,
    defaultValue,
    item_name,
    dc_item_spec,
    dc_item_unit,
    nm_model,
    dc_rmk,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    searchRef,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnItemDetailBox2Props) => {
    const { userInfo } = global();
    const [value, setValue] = useState(defaultValue);
    const [displayValue, setDisplayValue] = useState(defaultValue?.code || "");
    const [itemData, setItemData] = useState({
      code: defaultValue?.code || "",
      name: item_name || "",
      spec: dc_item_spec || "",
      unit: dc_item_unit || "",
      model: nm_model || "",
      rmk: dc_rmk || "",
    });

    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [backgroundColor, setBackgroundColor] = useState(
      isEditable ? "white" : "#F1F1F1"
    );

    const { runOnSearch } = onSearchStore();

    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);
    // const [clearValue, setClearValue] = useState(false);

    // 팝업 세팅용
    const [title, setTitle] = useState("");
    const [width, setWidth] = useState<any>();
    const [height, setHeight] = useState<any>();
    const [autoSearch, setAutoSearch] = useState(true);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    useEffect(() => {
      const singlePopupData = getSearchData(code, undefined);
      setTitle(singlePopupData.title);
      setAutoSearch(singlePopupData.autoSearch);
      setWidth(singlePopupData.width);
      setHeight(singlePopupData.height);
    }, [code]);

    useEffect(() => {
      if (defaultValue) {
        setValue(defaultValue);
        if (isFocused) {
          setDisplayValue(defaultValue?.code || "");
        } else {
          setDisplayValue(defaultValue?.code || "");
        }
      }
    }, [defaultValue]);

    useEffect(() => setBorderWidth(isRequired ? "2px" : "1px"), [isRequired]);

    useEffect(
      () => setBackgroundColor(isEditable ? "white" : "#F1F1F1"),
      [isEditable]
    );

    const openPopup = async (inputValue?: string) => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const formData = searchRef?.current?.getFormdata() || {};
        const mergedConditions = popupConditions
          ? { ...formData, ...popupConditions }
          : formData;

        const mergedData = {
          ...mergedConditions,
          str_search: inputValue || displayValue,
          cd_clas: cd_clas || "",
        };

        let data: any;

        const result = await showPopupModal({
          title: title,
          children: (
            <SinglePopup
              code={code}
              selectedData={mergedData}
              autoSearch={autoSearch}
            />
          ),
          width: width,
          height: height,
        });
        data = handleReturnFunction(result);
        if (data === "close") {
          // setValue({ code: displayValue, name: "" });
          // setDisplayValue(displayValue);
          // onValueChanged?.(displayValue, "");
          // onFocus && onFocus("current");
          return;
        } else if (data.length > 0) {
          setValue(data[0]);
          const newData = {
            code: data[0].code,
            name: data[0].nm_item,
            spec: data[0].dc_item_spec || "",
            unit: data[0].dc_item_unit || "",
            model: data[0].nm_model || "",
            rmk: data[0].dc_rmk || "",
          };
          setItemData(newData);

          if (isSearchPanel) {
            setDisplayValue(data[0].code);
            onValueChanged?.(data[0].code, data[0].name);
            onFocus && onFocus("current");
          } else {
            setDisplayValue(data[0].code);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            onFocus && onFocus("next");
          }
        } else {
          // 팝업에서 취소 눌렀을 때
          clearValue();
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setFocused(true);
      setDisplayValue(value.code);
    };

    const handleBlur = async (key?: string) => {
      setFocused(true);

      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (code === "POPUP_POSTCODE") {
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          if (key === "Tab") onFocus && onFocus("next");
        }
      } else {
        if (!displayValue) {
          // 입력값이 없을 때
          if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
            await openPopup();
          } else {
            // setClearValue(true);
            clearValue();
            if (key === "Tab") onFocus && onFocus("next");
          }
        } else {
          // 입력값이 있을 때
          if (key === "searchBtn" && !isOpenRef.current) {
            await openPopup(value.code || displayValue);
          } else {
            const data = await checkData();
            if (data && data.length === 1) {
              setValue(data[0]);
              onValueChanged?.(data[0].code, data[0].name, data[0]);
              if (key === "Enter") {
                if (isSearchPanel) {
                  setDisplayValue(data[0].code);

                  const newData = {
                    code: data[0].code,
                    name: data[0].nm_item,
                    spec: data[0].dc_item_spec || "",
                    unit: data[0].dc_item_unit || "",
                    model: data[0].nm_model || "",
                    rmk: data[0].dc_rmk || "",
                  };
                  setItemData(newData);
                  setFocused(true);
                  onFocus && onFocus("current");
                } else {
                  setDisplayValue(data[0].code);
                  const newData = {
                    code: data[0].code,
                    name: data[0].nm_item,
                    spec: data[0].dc_item_spec || "",
                    unit: data[0].dc_item_unit || "",
                    model: data[0].nm_model || "",
                    rmk: data[0].dc_rmk || "",
                  };
                  setItemData(newData);
                  onFocus && onFocus("next");
                }
              } else if (key === "Tab") {
                setDisplayValue(data[0].code);
                const newData = {
                  code: data[0].code,
                  name: data[0].nm_item,
                  spec: data[0].dc_item_spec || "",
                  unit: data[0].dc_item_unit || "",
                  model: data[0].nm_model || "",
                  rmk: data[0].dc_rmk || "",
                };
                setItemData(newData);
                onFocus && onFocus("next");
              } else {
                setDisplayValue(data[0].code);
                const newData = {
                  code: data[0].code,
                  name: data[0].nm_item,
                  spec: data[0].dc_item_spec || "",
                  unit: data[0].dc_item_unit || "",
                  model: data[0].nm_model || "",
                  rmk: data[0].dc_rmk || "",
                };
                setItemData(newData);
              }
              return data[0];
            } else if (!isOpenRef.current) {
              await openPopup(displayValue);
            }
          }
        }
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(e.target.value);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const result = await handleBlur(e.key);

        if (result?.code) {
          await runOnSearch();
        }
      } else if (e.key === "Tab") {
        e.preventDefault();
        await handleBlur(e.key);
      }
    };

    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        // if (!popupSetting || value?.code === displayValue) return;
        if (!popupSetting) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            [popupSetting.str_search]: displayValue,
            cd_clas: cd_clas || "",
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: {
            cd_firm: userInfo.cd_firm,
            str_search: displayValue,
            cd_clas: cd_clas || "",
          },
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        return data.map((d: any) => ({
          ...d,
          code: d[popupSetting.columns[0]],
          name: d[popupSetting.columns[1]],
        }));
      }
    };

    const handleClear = async (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // setClearValue(true);
      setTimeout(() => {
        clearValue();
      }, 0);
      onFocus && onFocus("current");
    };

    const clearValue = () => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      onValueChanged?.("", "", {});
      setValue({ code: "", name: "" });
      setDisplayValue("");
      const newData = {
        code: "",
        name: "",
        spec: "",
        unit: "",
        model: "",
        rmk: "",
      };
      setItemData(newData);
      // if (index && inputEls) inputEls.current[index].value = "";
    };

    const onLeft = async () => {
      const param = {
        id: "popup_item_07",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: popupConditions.cd_biz
            ? popupConditions.cd_biz
            : userInfo.cd_biz,
          cd_item: value.code,
        },
      };

      const data: any = await fnGetDataSet(param);
      if (data && data[0] && data[0][0]) {
        const next = data[0][0];

        const newData = {
          code: next.codeValue || next.cd_item,
          name: next.nm_item || "",
          spec: next.dc_item_spec || "",
          unit: next.dc_item_unit || "",
          model: next.nm_model || "",
          rmk: next.dc_rmk || "",
        };

        const nextCode = next.codeValue || next.cd_item;
        const nextName = next.nm_item || "";

        setItemData(newData);
        setValue({ code: nextCode, name: nextName });
        onValueChanged?.(next.cd_item, nextName, next);
      } else {
        setValue({ code: "", name: "" });
        onValueChanged?.("", "", {});
      }
      if (isSearchPanel) {
        await runOnSearch();
        onFocus && onFocus("current");
      }
    };

    const onRight = async () => {
      const param = {
        id: "popup_item_08",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: popupConditions.cd_biz
            ? popupConditions.cd_biz
            : userInfo.cd_biz,
          cd_item: value.code,
        },
      };
      const data: any = await fnGetDataSet(param);
      if (data && data[0] && data[0][0]) {
        const next = data[0][0];

        const newData = {
          code: next.codeValue || next.cd_item,
          name: next.nm_item || "",
          spec: next.dc_item_spec || "",
          unit: next.dc_item_unit || "",
          model: next.nm_model || "",
          rmk: next.dc_rmk || "",
        };

        const nextCode = next.codeValue || next.cd_item;
        const nextName = next.nm_item || "";

        setItemData(newData);
        setValue({ code: nextCode, name: nextName });
        onValueChanged?.(next.cd_item, nextName, next);
      } else {
        setValue({ code: "", name: "" });
        onValueChanged?.("", "", {});
      }
      if (isSearchPanel) {
        await runOnSearch();
        onFocus && onFocus("current");
      }
    };

    return (
      <div className="flex justify-center w-full" style={{ padding: 0 }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <SnLabel type="SearchPanel" text="품목" />
            <TextField
              inputRef={inputRef}
              value={displayValue}
              onFocus={handleFocus}
              onBlur={() => handleBlur()}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onMouseOver={() => setMouseOver(true)}
              onMouseOut={() => setMouseOver(false)}
              fullWidth
              slotProps={{
                input: {
                  autoComplete: "off",
                  endAdornment: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {(isFocused || isMouseOver) &&
                        !isReadOnly &&
                        (displayValue || itemData.name) && (
                          <IconButton
                            edge="end"
                            onClick={handleClear}
                            onMouseDown={(event) => event.preventDefault()}
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      <IconButton
                        onClick={(e) => {
                          onLeft();
                        }}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                      >
                        <ArrowBackIosNew fontSize="small" />
                      </IconButton>

                      <IconButton
                        onClick={(e) => {
                          openPopup();
                        }}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          onRight();
                        }}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                      >
                        <ArrowForwardIos fontSize="small" />
                      </IconButton>
                    </div>
                  ),
                },
              }}
              sx={{
                backgroundColor,
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
            <TextField
              value={itemData.name}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              fullWidth
              sx={{
                mr: 2,
                backgroundColor: "#dadcdc",
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
            <SnLabel type="SearchPanel" text="단위" />
            <TextField
              value={itemData.unit}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              sx={{
                width: "30%",
                backgroundColor: "#dadcdc",
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", width: "80%" }}>
            <SnLabel type="SearchPanel" text="규격" />
            <TextField
              value={itemData.spec}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              sx={{
                mr: 2,
                width: "57%",
                backgroundColor: "#dadcdc",
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
            <SnLabel type="SearchPanel" text="모델" />
            <TextField
              value={itemData.model}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              sx={{
                width: "43%",
                backgroundColor: "#dadcdc",
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", width: "80%" }}>
            <SnLabel type="SearchPanel" text="참고사항" />
            <TextField
              value={itemData.rmk}
              fullWidth
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              sx={{
                backgroundColor: "#dadcdc",
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
          </Box>
        </Box>
      </div>
    );
  }
);
export default SnItemDetailBox2;
